import { formatCodeableConcept, type WithId } from '@medplum/core';
import type { Immunization } from '@medplum/fhirtypes';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS } from '../../utils/date';
import { type PromotionStatus, RecordListItem } from './RecordListItem';

export type ImmunizationsListProps = {
  immunizations: WithId<Immunization>[];
  onSelectImmunization?: (id: string) => void;
  selectedImmunizationId?: string;
  onPromotionClick?: (id: string) => void;
  getPromotionStatus?: (id: string) => PromotionStatus;
};

export const ImmunizationsList = ({
  immunizations,
  onSelectImmunization,
  selectedImmunizationId,
  onPromotionClick,
  getPromotionStatus,
}: ImmunizationsListProps): React.ReactElement => {
  const { t } = useTranslation('records');

  return (
    <div className="mx-auto w-full max-w-4xl">
      <div className="space-y-1">
        {immunizations.length > 0 ? (
          immunizations.map((immunization) => {
            const name = formatCodeableConcept(immunization.vaccineCode);
            const description = `${t('statusOnDate', {
              status: immunization.status ?? t('unknown'),
              date: immunization.occurrenceDateTime
                ? format(parseISO(immunization.occurrenceDateTime), DATE_FORMATS.DATE)
                : t('unknown'),
            })}${immunization.lotNumber ? ` · ${immunization.lotNumber}` : ''}`;

            return (
              <RecordListItem
                key={immunization.id}
                title={name || t('unknownImmunization')}
                description={description}
                onClick={onSelectImmunization ? (): void => onSelectImmunization(immunization.id) : undefined}
                selected={selectedImmunizationId === immunization.id}
                onPromotionClick={onPromotionClick ? (): void => onPromotionClick(immunization.id) : undefined}
                promotionStatus={getPromotionStatus?.(immunization.id)}
              />
            );
          })
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('noImmunizationsFound')}</p>
        )}
      </div>
    </div>
  );
};
